import { createSlice } from '@reduxjs/toolkit';
import {
  fetchOrgRolesThunk,
  createOrgRoleThunk,
  updateOrgRoleThunk,
  deleteOrgRoleThunk,
  fetchOrgRoleHoldersThunk,
  mapUserToOrgRoleThunk,
  unmapUserFromOrgRoleThunk,
} from './orgRolesActionThunks';

const initialState = {
  items: [],
  loading: { items: false, mutate: false },
  errors: { items: null, mutate: null, holders: null },
  // Keyed by org_role id -- who currently occupies that seat.
  holdersByRole: {},
};

const orgRolesSlice = createSlice({
  name: 'orgRoles',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrgRolesThunk.pending, (state) => {
        state.loading.items = true;
        state.errors.items = null;
      })
      .addCase(fetchOrgRolesThunk.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading.items = false;
      })
      .addCase(fetchOrgRolesThunk.rejected, (state, action) => {
        state.loading.items = false;
        state.errors.items = action.payload || 'Failed to load org roles';
      })

      .addCase(createOrgRoleThunk.pending, (state) => {
        state.loading.mutate = true;
        state.errors.mutate = null;
      })
      .addCase(createOrgRoleThunk.fulfilled, (state, action) => {
        state.loading.mutate = false;
        state.items.push(action.payload);
        state.items.sort((a, b) => a.sort_order - b.sort_order);
      })
      .addCase(createOrgRoleThunk.rejected, (state, action) => {
        state.loading.mutate = false;
        state.errors.mutate = action.payload || 'Failed to create org role';
      })

      .addCase(updateOrgRoleThunk.fulfilled, (state, action) => {
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(updateOrgRoleThunk.rejected, (state, action) => {
        state.errors.mutate = action.payload || 'Failed to update org role';
      })

      .addCase(deleteOrgRoleThunk.fulfilled, (state, action) => {
        state.items = state.items.filter((i) => i.id !== action.payload);
      })
      .addCase(deleteOrgRoleThunk.rejected, (state, action) => {
        state.errors.mutate = action.payload || 'Failed to delete org role';
      })

      .addCase(fetchOrgRoleHoldersThunk.fulfilled, (state, action) => {
        const { roleId, holders } = action.payload;
        state.holdersByRole[roleId] = holders;
      })
      .addCase(fetchOrgRoleHoldersThunk.rejected, (state, action) => {
        state.errors.holders = action.payload || 'Failed to load role holders';
      })
      .addCase(mapUserToOrgRoleThunk.fulfilled, (state, action) => {
        const { roleId, holder } = action.payload;
        const existing = state.holdersByRole[roleId] || [];
        if (!existing.some((h) => h.user_id === holder.user_id)) {
          state.holdersByRole[roleId] = [...existing, holder];
        }
      })
      .addCase(mapUserToOrgRoleThunk.rejected, (state, action) => {
        state.errors.holders = action.payload || 'Failed to map user to role';
      })
      .addCase(unmapUserFromOrgRoleThunk.fulfilled, (state, action) => {
        const { roleId, userId } = action.payload;
        state.holdersByRole[roleId] = (state.holdersByRole[roleId] || []).filter(
          (h) => h.user_id !== userId
        );
      })
      .addCase(unmapUserFromOrgRoleThunk.rejected, (state, action) => {
        state.errors.holders = action.payload || 'Failed to unmap user from role';
      });
  },
});

export default orgRolesSlice.reducer;
